import { Flex, MenuItem } from "@chakra-ui/react";
import { getCapitalActivityEventStateBadge } from "features/capital-activity/CapitalActivityUtils";
import { CapitalActivityAdjustmentsModal } from "features/capital-activity/event/CapitalActivityAdjustmentsModal";
import { CapitalEventStatsCard } from "features/capital-activity/event/CapitalEventStatsCard";
import DistributionReadOnlyDrawdownTable from "features/distributions/DistributionReadOnlyDrawdownTable";
import { FundFamilyDistributionBreakdownTable } from "features/distributions/FundFamilyDistributionBreakdownPage";
import {
  useFundFamilyEventRetrieveSuspense,
  useFundFamilySubEventList,
} from "gen/endpoints/fund-family-event/fund-family-event";
import { AdjustableFundFamilySubEventTypeEnum } from "gen/model/adjustableFundFamilySubEventTypeEnum";
import { FundFamilyEventAPIData } from "gen/model/fundFamilyEventAPIData";
import { FundFamilyEventStateMachineFieldsEnum } from "gen/model/fundFamilyEventStateMachineFieldsEnum";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Restricted } from "shared/components/access/Restricted";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { PageMenu } from "shared/components/layout/PageMenu";
import {
  PageWizardProvider,
  PageWizardRoutes,
  usePageWizard,
} from "shared/components/layout/PageWizard";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { useFeatureFlagActive } from "shared/hooks/useFeatureFlag";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";

export const FundFamilyDistributionPage: React.FC = () => {
  const { t } = useTranslation(["common", "distribution"]);
  const navigate = useNavigate();

  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const activityEventPath = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .capitalActivity({})
    .capitalActivityEvent({ fundFamilyEventId }).$;

  if (eventData.state !== FundFamilyEventStateMachineFieldsEnum.draft) {
    return (
      <PageContent isFullHeight={true}>
        <PageHeader
          title={t("distribution:header.title")}
          badge={getCapitalActivityEventStateBadge(eventData.state, t, false)}
          backButton={{
            title: t("common:capitalEvent"),
            path: activityEventPath,
          }}
          stats={<CapitalEventStatsCard capitalActivityData={eventData} />}
        />
        <PageBody>
          <Flex direction="column" gap="24px">
            <DistributionReadOnlyDrawdownTable
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={fundFamilyEventId}
            />
            <FundFamilyDistributionBreakdownTable
              fundFamilyId={fundFamilyId}
              fundFamilyEventId={fundFamilyEventId}
            />
          </Flex>
        </PageBody>
      </PageContent>
    );
  }

  const navigateToActivityEvent = () => {
    navigate(activityEventPath);
  };

  return (
    <PageWizardProvider
      finishTitle={t("distribution:footer.finalizeDistribution")}
      onFinish={navigateToActivityEvent}
      onExit={navigateToActivityEvent}
    >
      {/* NOTE: Can't set isFullHeight={true} because the main table in
          FundFamilyDistributionAllocationPage does not have enough
          space when the Fee Holdback card is visible. */}
      <PageContent>
        <FundFamilyDistributionPageTitle eventData={eventData} />
        <PageWizardRoutes />
      </PageContent>
    </PageWizardProvider>
  );
};

const FundFamilyDistributionPageTitle: React.FC<{
  eventData: FundFamilyEventAPIData;
}> = ({ eventData }) => {
  const { t } = useTranslation(["common", "distribution", "capitalActivity"]);
  const { currentPage } = usePageWizard();
  const isFrontendAdjustmentsFeatureFlagActive = useFeatureFlagActive(
    "frontend_adjustments",
  );

  const { data: subEventDataList } = useFundFamilySubEventList(
    eventData.fund_family_id,
    eventData.id!,
  );

  const distributionSubEvent = subEventDataList?.find(
    (subEventData) => subEventData.type === "distribution",
  );

  const openAddAdjustmentsModal = useConfirmationDialog(
    CapitalActivityAdjustmentsModal,
    {
      fundFamilyEvent: eventData,
      subEventTypeBeingAdjusted:
        AdjustableFundFamilySubEventTypeEnum.distribution,
    },
  );

  return (
    <PageHeader
      title={currentPage!.title}
      workflowName={t("common:distribution")}
      badge={getCapitalActivityEventStateBadge(eventData.state, t)}
      actions={
        isFrontendAdjustmentsFeatureFlagActive && distributionSubEvent ? (
          <Restricted permission={PermissionTypeEnum.write}>
            <PageMenu>
              <MenuItem onClick={openAddAdjustmentsModal}>
                {t("capitalActivity:adjustments.title")}
              </MenuItem>
            </PageMenu>
          </Restricted>
        ) : undefined
      }
    />
  );
};
